import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck, FileText } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useSeo } from "@/lib/useSeo";
import { LogoMark } from "@/components/brand/Logo";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";

export default function ProposalView() {
  const { id } = useParams();
  const { data: p, isLoading, isError, refetch } = useQuery({
    queryKey: ["proposal", id], queryFn: async () => (await api.get(`/proposals/${id}`)).data, retry: false,
  });
  useSeo({ title: p ? `Proposal ${p.proposal_no}` : "Proposal", description: "Review and accept your Buddy Solution proposal." });
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);

  const fmt = (n) => p ? `${p.currency} ${Number(n || 0).toLocaleString()}` : n;

  const accept = async () => {
    if (!terms) { toast.error("Please accept the terms."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/proposals/${id}/accept`, { accept_terms: true });
      toast.success(data.message || "Proposal accepted");
      refetch();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (isLoading) return <div className="bs-container py-24 text-center text-muted-foreground">Loading proposal...</div>;
  if (isError || !p) return (
    <div className="bs-container py-24 text-center"><p className="text-muted-foreground">Proposal not found.</p>
      <Button asChild className="mt-4 rounded-full"><Link to="/advertising">Build a new quote</Link></Button></div>
  );

  const accepted = p.status === "Accepted" || p.status === "Paid";

  return (
    <div className="bs-container py-12 pb-64 lg:py-16 lg:pb-72">
      <div className="mx-auto max-w-3xl">
        <div className="overflow-hidden rounded-3xl border border-border bg-card shadow-soft">
          <div className="flex items-center justify-between gap-3 border-b border-border bg-[#0B0E14] p-6 text-white">
            <div className="flex items-center gap-3"><LogoMark size={40} /><div><p className="font-display text-lg font-extrabold">Buddy Solution</p><p className="text-xs text-white/60">Proposal {p.proposal_no}</p></div></div>
            <Badge className={accepted ? "bg-success text-white" : "bg-primary text-white"} data-testid="proposal-status">{p.status}</Badge>
          </div>

          <div className="p-6 sm:p-8">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Prepared for" value={`${p.name}${p.company ? ` · ${p.company}` : ""}`} />
              <Field label="Email" value={p.email} />
              <Field label="Package" value={p.package_name} />
              <Field label="Platform" value={p.platform || "—"} />
              <Field label="Management duration" value={`${p.duration_months} month(s)`} />
              <Field label="Valid until" value={new Date(p.expiry_at).toLocaleDateString()} />
            </div>

            <h3 className="mt-8 font-display text-lg font-bold">Cost breakdown</h3>
            <div className="mt-3 space-y-2 rounded-xl border border-border p-4 text-sm">
              <Row label={`Service fee (${fmt(p.monthly_fee)} × ${p.duration_months})`} value={p.is_custom ? "Custom" : fmt(p.service_fee_total)} />
              {p.addons?.map((a) => <Row key={a.id} label={`Add-on: ${a.name}`} value={fmt(a.price)} />)}
              <Row label="Subtotal" value={p.is_custom ? "Custom" : fmt(p.subtotal)} />
              {p.tax_percent > 0 && <Row label={`${p.tax_label} (${p.tax_percent}%)`} value={fmt(p.tax_amount)} />}
              <div className="my-1 h-px bg-border" />
              <div className="flex items-center justify-between text-base font-bold"><span>Total service cost</span><span data-testid="proposal-total">{p.is_custom ? "Custom quote" : fmt(p.total)}</span></div>
            </div>

            <div className="mt-4 rounded-xl border border-accent/30 bg-accent/5 p-4 text-sm text-muted-foreground">
              <p className="flex items-start gap-2"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                Estimated advertising platform budget: <strong className="text-foreground">{fmt(p.ad_budget * p.duration_months)}</strong> ({fmt(p.ad_budget)}/mo), billed by the platform and controlled by you. Not included in the service fee. No leads, sales or ROI are guaranteed.</p>
            </div>

            {p.terms && <div className="mt-6"><h4 className="flex items-center gap-2 font-display font-bold"><FileText className="h-4 w-4" /> Terms</h4><p className="mt-2 text-xs leading-relaxed text-muted-foreground">{p.terms}</p></div>}

            {accepted ? (
              <div className="mt-6 rounded-xl border border-success/30 bg-success/5 p-5 text-center" data-testid="proposal-accepted">
                <CheckCircle2 className="mx-auto h-8 w-8 text-success" />
                <p className="mt-2 font-semibold">Proposal accepted</p>
                <p className="mt-1 text-sm text-muted-foreground">Payment integration requires configuration. Our team will contact you to proceed and begin campaign setup.</p>
                <Button asChild className="mt-4 rounded-full"><Link to="/dashboard">Go to dashboard</Link></Button>
              </div>
            ) : (
              <div className="mt-6 space-y-3 border-t border-border pt-5">
                <label className="flex items-start gap-2 text-sm text-muted-foreground">
                  <Checkbox checked={terms} onCheckedChange={setTerms} className="mt-0.5" data-testid="proposal-terms" />
                  I have read and accept the terms, and I authorize Buddy Solution to proceed with this proposal.
                </label>
                <Button onClick={accept} size="lg" className="w-full rounded-full shadow-glow" disabled={busy} data-testid="proposal-accept">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Accept Proposal
                </Button>
                <p className="text-center text-xs text-muted-foreground">Payment is requested only after acceptance, once payment integration is configured.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }) { return <div><p className="text-xs text-muted-foreground">{label}</p><p className="font-semibold">{value}</p></div>; }
function Row({ label, value }) { return <div className="flex items-center justify-between text-muted-foreground"><span>{label}</span><span className="font-medium text-foreground">{value}</span></div>; }
